/**
 * Inline sparkline for KPI tiles. Pure SVG — no dependencies, no tooltips,
 * no axes. Renders a 6-week mini line with the last point highlighted.
 */
import React, { useMemo } from 'react';
import { CEDAR } from '../theme';

export interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
  stroke?: string;
  fill?: string;
  className?: string;
  /** Show the last point as a filled dot. */
  showEndpoint?: boolean;
}

export const Sparkline: React.FC<SparklineProps> = ({
  values,
  width = 120,
  height = 32,
  stroke = CEDAR.stream,
  fill = `${CEDAR.stream}22`, // ~13% alpha
  className,
  showEndpoint = true,
}) => {
  const { linePath, areaPath, endX, endY, hasData } = useMemo(() => {
    if (!values || values.length < 2) {
      return { linePath: '', areaPath: '', endX: 0, endY: 0, hasData: false };
    }
    const pad = 2;
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;
    const stepX = (width - pad * 2) / (values.length - 1);
    const points = values.map((v, i) => {
      const x = pad + i * stepX;
      const y = pad + (height - pad * 2) * (1 - (v - min) / range);
      return [x, y] as const;
    });
    const line = points.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(' ');
    const area = `${line} L${points[points.length - 1][0]},${height} L${points[0][0]},${height} Z`;
    const [lastX, lastY] = points[points.length - 1];
    return { linePath: line, areaPath: area, endX: lastX, endY: lastY, hasData: true };
  }, [values, width, height]);

  if (!hasData) {
    return (
      <svg width={width} height={height} className={className} aria-hidden>
        <line
          x1={2}
          y1={height / 2}
          x2={width - 2}
          y2={height / 2}
          stroke="var(--border)"
          strokeWidth={1.5}
          strokeDasharray="3 3"
        />
      </svg>
    );
  }

  return (
    <svg width={width} height={height} className={className} aria-hidden>
      <path d={areaPath} fill={fill} />
      <path d={linePath} stroke={stroke} strokeWidth={1.75} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      {showEndpoint && (
        <circle cx={endX} cy={endY} r={2.5} fill={stroke} />
      )}
    </svg>
  );
};
